import { useId } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Picture, PictureProps } from "@/components/wpl/image/src";
import { wacToken } from "@/libraries/design/foundations";
import {
    WCL_GRID_MAX_WIDTH,
    WCL_GRID_PADDING_LG
} from "@/libraries/design/foundations/grid/src/base";
import { RichTextComponent } from "@/libraries/design/utils";

import {
    Button,
    ButtonProps,
    ButtonSize,
    ButtonVariant
} from "../../button/src/button";
import { Image } from "../../image/src/image";
import { Link, LinkProps, BgColors } from "../../link/src/link";
export interface ButtonAndLinkProps {
    button: ButtonProps;
    buttonText: string;
    buttonVariant?: ButtonVariant;
    link?: LinkProps;
}

export interface OneLinkProps {
    link: LinkProps;
}

export type ProductCardBlockCtaProps = ButtonAndLinkProps | OneLinkProps;

export type ProductCardBlockTitleAs = "h2" | "h3" | "h4";
export type ProductCardBlockAlignment = "start" | "center";

export type ResponsiveProductCardBlockAlignment = {
    mobile: ProductCardBlockAlignment;
    tablet: ProductCardBlockAlignment;
    desktop: ProductCardBlockAlignment;
};

export type IconProps = {
    linkedIcon?: {
        url: string;
        size?: keyof typeof iconSizes;
        alt?: string;
    };
    iconComponent?: JSX.Element;
};

export type ProductCardBlockColumns = 10 | 12; // Currently only 10 or 12 columns are supported

export const ProductCardBlockLogoLockups = {
    jira: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/1m4xip711l01021jmk8k33gj82grm078.svg",
        height: 32,
        alt: "Jira logo"
    },
    jiraProductDiscovery: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/lnuk8307fp65406pfvj4radajgkw48c5.svg",
        height: 32,
        alt: "Jira Product Discovery logo"
    },
    jiraServiceManagement: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/1x20y6tg7hdjl45x6l8a4iy22n3vhff2.svg",
        height: 32,
        alt: "Jira Service Management logo"
    },
    statuspage: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/r237roxi4i14gj1i4t362kw1g06h63tv.svg",
        height: 32,
        alt: "Statuspage logo"
    },
    confluence: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/ou687o50873egmj4l236et86h6178ylm.svg",
        height: 32,
        alt: "Confluence logo"
    },
    trello: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/gf3q277c3idu533i28bg3855yp2mwrlg.svg",
        height: 32,
        alt: "Trello logo"
    },
    loom: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/5ncpek557ni2al3s7s5v0dmkcs4ogt05.svg",
        height: 32,
        alt: "Loom logo"
    },
    bitbucket: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/xqweg4il0228mpa8ega4i5hi78p0k16v.svg",
        height: 32,
        alt: "Bitbucket logo"
    },
    rovoDev: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/d888ipg7v6w1c6x4xfb578gfiw5238d0.svg",
        height: 32,
        alt: "Rovo Dev logo"
    },
    sourcetree: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/01u3w2aypa025m62s70p8uo6u21qg3i2.svg",
        height: 32,
        alt: "Sourcetree logo"
    },
    compass: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/yiu46gin8eoxy7f57m1g76a6b22uuf1b.svg",
        height: 32,
        alt: "Compass logo"
    },
    guard: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/7085415d2l112j5j545y8nv6ir6t262w.svg",
        height: 32,
        alt: "Guard logo"
    },
    teamworkCollection: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/78ylq28783ef6w7c21vu5y84ngku10fx.svg",
        height: 32,
        alt: "Teamwork Collection logo"
    },
    strategyCollection: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/h5mj8675hs2i21g7687jagjoax1uy14n.svg",
        height: 32,
        alt: "Strategy Collection logo"
    },
    serviceCollection: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/f481qls2e2g883b2us2ek546yg4c44g1.svg",
        height: 32,
        alt: "Service Collection logo"
    },
    softwareCollection: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/1j5jdr1kj625ayo48v421j3o05hg8nkg.svg",
        height: 32,
        alt: "Software Collection logo"
    },
    focus: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/q2h38a2vw336a1m16nstqc8s7w50t820.svg",
        height: 32,
        alt: "Focus logo"
    },
    talent: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/241h1u1khig63kccl1fe3hisqom3q007/fl_keep_metadata/Apps-Talent.svg",
        height: 32,
        alt: "Talent logo"
    },
    rovo: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/v878apvhek0q57kko33jlhu74xk3hvnx.svg",
        height: 32,
        alt: "Rovo logo"
    },
    jiraAlign: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/wt00m6668k5546116m0mii0b24q5iv2j/fl_keep_metadata/Apps-light_Jira-Align_Logo_brand_RGB.svg",
        height: 32,
        alt: "Jira Align logo"
    },
    pipelines: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/ph5f4g2e315835kmw365b8210i128f6o.svg",
        height: 32,
        alt: "Pipelines logo"
    },
    dx: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/17q1f1j46us1tot0q6y7h67p44ih4548.svg",
        height: 32,
        alt: "DX logo"
    },
    assets: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/kb7rbd47j72qq3p7a02s87hcv743h5p6.svg",
        height: 32,
        alt: "Assets logo"
    },
    customerServiceManagement: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/fhyy25ehhmv4u3mibqncq66x00s65l6t.svg",
        height: 32,
        alt: "Customer Service Management logo"
    },
    bamboo: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/5cisniv00mcsj58818l5045tbqo7hfpp.svg",
        height: 32,
        alt: "Bamboo logo"
    },
    crowd: {
        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/itn2206el7o5j3h62x6bi628p40vcjpe.svg",
        height: 32,
        alt: "Crowd logo"
    }
};

export interface ProductCardBlockProps {
    logo?: keyof typeof ProductCardBlockLogoLockups;
    eyebrow?: string;
    title?: string;
    text?: string | React.ReactElement<typeof RichTextComponent>;
    titleAs?: ProductCardBlockTitleAs;
    pictureProps?: PictureProps;
    spotIllustrationUrl?: string;
    icon?: IconProps;
    callToActionProps?: ProductCardBlockCtaProps;
    alignment?: ProductCardBlockAlignment | ResponsiveProductCardBlockAlignment;
    orientation?: "horizontal" | "vertical" | "horizontal-reverse";
    columns?: ProductCardBlockColumns;
    backgroundColor?: string;
}

interface RenderCtaProps {
    ctaProps: ProductCardBlockCtaProps;
    buttonSize: ButtonSize;
    alignment: ProductCardBlockAlignment | ResponsiveProductCardBlockAlignment;
    backgroundColor?: string;
    titleId?: string;
    callToActionId?: string;
}

const imageStyle = css({
    // Issue in Picture component applies styles to picture instead of img
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        aspectRatio: "16 / 9",
        width: "100%",
        height: "auto",
        objectFit: "contain"
    }
});

const zeroMarginCss = css({ margin: 0 });

const hyphenStyles = css({
    hyphens: "auto"
});

const iconSizes = {
    "x-small": 24,
    "small": 32,
    "medium": 48,
    "large": 64
};

type ProductCardBlockColumnsInfo = "assetSpan" | "contentStart";

const contentColumnsLayout: Record<
    ProductCardBlockColumns,
    Record<ProductCardBlockColumnsInfo, number>
> = {
    10: { assetSpan: 3, contentStart: 2 }, // 10 columns content block means 3 columns for asset, 7 for text
    12: { assetSpan: 4, contentStart: 1 }
    // Add more mappings as needed in the future, ex:
    // 8: 2 // 8 columns content block means 2 columns for asset, 6 for text
};

const renderCta = ({
    ctaProps,
    buttonSize,
    alignment,
    backgroundColor,
    titleId,
    callToActionId
}: RenderCtaProps) => {
    if ("button" in ctaProps && ctaProps.button !== undefined) {
        return (
            <div
                css={css({
                    margin: 0,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: token("space.250"),
                    alignItems: "center",
                    justifyContent:
                        typeof alignment === "string"
                            ? alignment
                            : alignment.mobile,
                    [media.above.sm]: {
                        justifyContent:
                            typeof alignment === "string"
                                ? alignment
                                : alignment.tablet
                    },
                    [media.above.md]: {
                        justifyContent:
                            typeof alignment === "string"
                                ? alignment
                                : alignment.desktop
                    }
                })}
            >
                <Button
                    {...ctaProps.button}
                    size={buttonSize}
                    variant={
                        backgroundColor &&
                        backgroundColor === "#1868DB" &&
                        (ctaProps.buttonVariant === "primary" ||
                            !ctaProps.buttonVariant)
                            ? "secondary"
                            : ctaProps.buttonVariant ?? "primary"
                    }
                    colorAgainst={
                        backgroundColor === "#1868DB" ? "other" : "white"
                    }
                    css={css({ flexBasis: "max-content" })}
                >
                    {ctaProps.buttonText}
                </Button>
                {ctaProps.link && (
                    // eslint-disable-next-line @atlassian/a11y/anchor-has-content -- Link component renders text content via the text prop from ctaProps.link
                    <Link
                        id={callToActionId}
                        ariaLabelledby={`${callToActionId} ${titleId}`}
                        type="primary"
                        bgColor={
                            (isDarkBackgroundColor(backgroundColor)
                                ? "bgBlackBlue"
                                : "bgWhite") as BgColors
                        }
                        {...ctaProps.link}
                    />
                )}
            </div>
        );
    }

    return (
        <div css={zeroMarginCss}>
            {/* @ts-ignore */}
            {/* eslint-disable-next-line @atlassian/a11y/anchor-has-content -- Link component renders text content via the text prop from ctaProps.link */}
            <Link
                id={callToActionId}
                ariaLabelledby={`${callToActionId} ${titleId}`}
                type="primary"
                bgColor={
                    (isDarkBackgroundColor(backgroundColor)
                        ? "bgBlackBlue"
                        : "bgWhite") as BgColors
                }
                {...ctaProps.link}
            />
        </div>
    );
};

const renderIcon = (icon: IconProps) => {
    if (icon?.linkedIcon) {
        const linkedIconSize = icon?.linkedIcon?.size
            ? iconSizes[icon.linkedIcon.size]
            : iconSizes["medium"];

        return (
            <div>
                <Picture
                    image={{
                        src: icon.linkedIcon.url,
                        alt: icon.linkedIcon.alt ?? "",
                        width: linkedIconSize,
                        height: linkedIconSize
                    }}
                />
            </div>
        );
    }
    if (icon?.iconComponent) {
        return <div>{icon.iconComponent}</div>;
    }
    return null;
};

const isDarkBackgroundColor = (color?: string): boolean => {
    return color ? ["#1868DB", "#101214"].includes(color) : false;
};

export const ProductCardBlock = ({
    logo,
    eyebrow,
    title,
    text,
    pictureProps,
    spotIllustrationUrl,
    icon,
    callToActionProps,
    alignment = "start",
    orientation = "vertical",
    columns = 10,
    backgroundColor,
    ...props
}: // eslint-disable-next-line complexity -- ProductCardBlock component has high complexity due to multiple layout variations and conditional rendering
ProductCardBlockProps) => {
    // hedge against the value being passed in as a string (the options in uniform can only be string values)
    // which would subsequently fail a string vs number check ie 10 === "10" is false
    const numColumns = Number(columns) as ProductCardBlockColumns;

    const { titleAs: TitleComponent = "h3" } = props;

    const isSmallDetailStyling = ["h4"].includes(TitleComponent as string);
    const isLargeTextStyling = (TitleComponent as string) === "h2";
    const buttonSize: ButtonSize = isSmallDetailStyling ? "small" : "large";

    const uniqueId = useId();
    const titleId =
        (title ? title.replace(/\s+/g, "") : "title") + `-${uniqueId}`;
    const callToActionId =
        (callToActionProps?.link
            ? callToActionProps.link.text.replace(/\s+/g, "")
            : "call-to-action") + `-${uniqueId}`;

    const lightTextStyles = css({
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "p": {
            color: `${wacToken("color.neutral.light0")}`
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".body-lg, .body-md": {
            color: `${wacToken("color.neutral.light0")}`
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "h2, h3, h4": {
            color: `${wacToken("color.neutral.light0")}`
        }
    });

    const textElement: React.ReactNode =
        typeof text === "string" ? (
            <p
                className={isLargeTextStyling ? "body-lg" : "body-md"}
                css={[
                    zeroMarginCss,
                    isDarkBackgroundColor(backgroundColor) && lightTextStyles
                ]}
            >
                {text}
            </p>
        ) : (
            text
        );

    const bgColorStyle = css({
        "--dxp-block-background-color": backgroundColor
    });

    const textContent = (
        <div
            css={[
                css({
                    display: "flex",
                    flexDirection: "column",
                    gap: token("space.300"),
                    paddingBlockStart:
                        orientation === "horizontal"
                            ? token("space.100")
                            : undefined,
                    justifyContent: "space-between",
                    [media.above.sm]: {
                        gridColumn: "var(--text-grid-column)"
                    },
                    height: "100%"
                }),
                isDarkBackgroundColor(backgroundColor) && lightTextStyles
            ]}
        >
            <div
                css={css({
                    display: "flex",
                    flexDirection: "column",
                    gap: token("space.300")
                })}
            >
                {logo && (
                    <Image
                        src={ProductCardBlockLogoLockups[logo].src}
                        alt={ProductCardBlockLogoLockups[logo].alt}
                        height={ProductCardBlockLogoLockups[logo].height}
                        style={{
                            objectPosition: "left top",
                            objectFit: "contain"
                        }}
                    />
                )}
                <div
                    className="product-card-block-text-content"
                    css={css({
                        display: "flex",
                        flexDirection: "column",
                        gap: token("space.100")
                    })}
                >
                    {eyebrow && (
                        <p
                            className={
                                isSmallDetailStyling
                                    ? "eyebrow-sm"
                                    : "eyebrow-md"
                            }
                            css={zeroMarginCss}
                        >
                            {eyebrow}
                        </p>
                    )}
                    {title && (
                        <TitleComponent
                            id={titleId}
                            css={[zeroMarginCss, hyphenStyles]}
                        >
                            {title}
                        </TitleComponent>
                    )}
                    {text && textElement}
                </div>
            </div>
            {callToActionProps &&
                renderCta({
                    ctaProps: callToActionProps,
                    buttonSize,
                    alignment,
                    backgroundColor,
                    titleId,
                    callToActionId
                })}
        </div>
    );

    const assetStyles = css({
        [media.above.sm]: {
            gridColumn: "var(--asset-grid-column)"
        }
    });

    const horizontalSpotIllustrationStyles = css({
        [media.above.sm]: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
        }
    });

    const assetContent = pictureProps ? (
        <div css={[assetStyles]}>
            <Picture {...pictureProps} css={imageStyle} />
        </div>
    ) : spotIllustrationUrl ? (
        <div css={[assetStyles, horizontalSpotIllustrationStyles]}>
            <Picture
                image={{
                    src: spotIllustrationUrl,
                    alt: "",
                    width: 236,
                    height: 236
                }}
            />
        </div>
    ) : icon ? (
        renderIcon(icon)
    ) : null;

    const gapBetweenIconAndText = css({
        gap: token("space.200")
    });

    const productCardPadding = css({
        padding: token("space.300")
    });

    if (orientation === "vertical") {
        return (
            <div
                css={[
                    bgColorStyle,
                    productCardPadding,
                    css({
                        textAlign:
                            typeof alignment === "string"
                                ? alignment
                                : alignment.mobile,
                        [media.above.sm]: {
                            textAlign:
                                typeof alignment === "string"
                                    ? alignment
                                    : alignment.tablet
                        },
                        [media.above.md]: {
                            textAlign:
                                typeof alignment === "string"
                                    ? alignment
                                    : alignment.desktop
                        },
                        display: "flex",
                        flexDirection: "column",
                        gap: token("space.300"), // for image or spot illustration
                        background:
                            "var(--dxp-block-background-color, transparent)"
                    }),
                    icon && gapBetweenIconAndText,
                    !backgroundColor && borderRadiusStyle
                ]}
            >
                {assetContent}
                {textContent}
            </div>
        );
    }

    const fullWidthGridStyles = css({
        "marginInline": "auto",
        "maxWidth": "100%",
        "width": "100%",
        "--width-without-gaps": "calc(100% - var(--cl-gap) * 11)", // width of only the 12 columns, subtracting the 11 gaps in 12 column layout
        [media.above.sm]: {
            gridTemplateColumns: "repeat(12, 1fr)"
        },
        [media.above.md]: {
            "--cl-gap": "32px",
            "maxWidth": `${WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2}px`
        },
        "display": "grid",
        "boxSizing": "border-box",
        "gap": "var(--cl-gap)",
        "--cl-gap": "24px"
    });

    const horizontalProductCard = (
        <div
            css={[
                productCardPadding,
                fullWidthGridStyles,
                bgColorStyle,
                css({
                    "position": "relative",
                    "zIndex": 1,
                    "--text-grid-column": `${
                        orientation === "horizontal-reverse"
                            ? contentColumnsLayout[numColumns]["contentStart"]
                            : contentColumnsLayout[numColumns]["contentStart"] +
                              contentColumnsLayout[numColumns]["assetSpan"]
                    } / span ${
                        numColumns -
                        contentColumnsLayout[numColumns]["assetSpan"]
                    }`,
                    "--asset-grid-column": `${
                        orientation === "horizontal"
                            ? contentColumnsLayout[numColumns]["contentStart"]
                            : contentColumnsLayout[numColumns]["contentStart"] +
                              numColumns -
                              contentColumnsLayout[numColumns]["assetSpan"]
                    } / span ${contentColumnsLayout[numColumns]["assetSpan"]}`
                }),
                !backgroundColor && borderRadiusStyle
            ]}
        >
            {orientation === "horizontal" ? assetContent : null}
            {textContent}
            {orientation === "horizontal-reverse" ? assetContent : null}
        </div>
    );

    switch (numColumns) {
        case 10:
        case 12:
            if (backgroundColor) {
                return (
                    <div
                        css={[
                            bgColorStyle,
                            css({
                                width: "100%",
                                position: "relative"
                            })
                        ]}
                    >
                        <div
                            css={[
                                css({
                                    position: "absolute",
                                    width: "100vw",
                                    [media.above.sm]: {
                                        width: `calc(${WCL_GRID_MAX_WIDTH}px + (${WCL_GRID_PADDING_LG}px * 2))`, // for when the grid width is stopped at its max with
                                        maxWidth: `min(calc(100% - ${WCL_GRID_PADDING_LG}px), calc(${WCL_GRID_MAX_WIDTH}px + 80px))` // for when the grid width is less than its max width, and to have background color max width at grid max width + 80px
                                    },
                                    height: "100%",
                                    insetInlineStart: "50%",
                                    transform: "translateX(-50%)",
                                    background:
                                        "var(--dxp-block-background-color, transparent)"
                                }),
                                borderRadiusStyle
                            ]}
                        />
                        {horizontalProductCard}
                    </div>
                );
            }
            return horizontalProductCard;
    }
};

const borderRadiusStyle = css({
    borderRadius: wacToken("corner.radius.card"),
    border: `1px solid ${wacToken("color.neutral.light400")}`
});
